import { useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { z } from 'zod'
import { useOidc } from '@/features/oidc/oidc'
import { persist } from 'zustand/middleware'
import { create } from 'zustand'

export const storeSearchSchema = z.object({
  store: z
    .union([z.literal('play-for-free'), z.literal('buy-gold-coins')])
    .optional(),
  'special-offer-item-set-id': z.string().optional(),
})

export type StoreSearchValue = z.infer<typeof storeSearchSchema>['store']

type StoreTabStore = {
  values: Record<string, StoreSearchValue>
  setTab: (tab: NonNullable<StoreSearchValue>, aid: string) => void
  getTab: (aid: string) => NonNullable<StoreSearchValue>
}

// We have to use store here to notify components that store tab value changed.
// Otherwise some components in other parts of react tree would not be re-rendered with a new tab parameter
const useStoreTabStore = create<StoreTabStore>()(
  persist(
    (set, get) => ({
      values: {},
      setTab: (tab, aid) => {
        const oldState = get()

        set({ values: { ...oldState.values, [aid]: tab } })
      },
      getTab: (aid: string) => get().values[aid] || 'play-for-free',
    }),
    {
      name: 'store-tab', // name of the item in the storage (must be unique)
    },
  ),
)

export const getStoreTabFromPersistence = (
  aid: string,
): NonNullable<StoreSearchValue> => {
  return useStoreTabStore.getState().getTab(aid)
}

export const saveStoreTabToPersistence = (
  aid: string,
  tab: NonNullable<StoreSearchValue>,
) => {
  useStoreTabStore.getState().setTab(tab, aid)
}

export const useStoreLaunch = () => {
  const navigate = useNavigate()

  const { decodedIdToken } = useOidc({ assert: 'user logged in' })
  const aid = decodedIdToken['pam-aid']

  const { getTab, setTab } = useStoreTabStore()

  const storeTab = getTab(aid)

  const close = useCallback(() => {
    return navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        store: undefined,
        'special-offer-item-set-id': undefined,
      }),
      resetScroll: false,
      replace: true,
    })
  }, [navigate])

  const open = useCallback(() => {
    return navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        store: storeTab,
      }),
      resetScroll: false,
      replace: true,
    })
  }, [navigate, storeTab])

  const changeTab = useCallback(
    (storeSearchValue: NonNullable<StoreSearchValue>) => {
      setTab(storeSearchValue, aid)
      return navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          store: storeSearchValue,
          'special-offer-item-set-id': undefined,
        }),
        resetScroll: false,
        replace: true,
      })
    },

    [navigate, aid, setTab],
  )

  return {
    open,
    close,
    changeTab,
    storeTab: getTab(aid),
  }
}
